import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Document, Page, pdfjs } from 'react-pdf'
import s from './PdfPreviewPortal.module.scss'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
	'pdfjs-dist/build/pdf.worker.min.mjs',
	import.meta.url,
).toString()

/**
 * In-page PDF preview (portal) using react-pdf for cross-device rendering.
 */
export default function PdfPreviewPortal({
	open,
	url,
	title = 'Document',
	onClose,
	onOpenInNewTab,
}) {
	const contentRef = useRef(null)
	const [pdfBlob, setPdfBlob] = useState(null)
	const [pageWidth, setPageWidth] = useState(null)
	const [numPages, setNumPages] = useState(null)
	const [pageNumber, setPageNumber] = useState(1)
	const [loadError, setLoadError] = useState('')

	useEffect(() => {
		if (!open) return
		setPageNumber(1)
		setNumPages(null)
		setLoadError('')
		setPdfBlob(null)
	}, [open, url])

	useEffect(() => {
		if (!open || !url) {
			setPdfBlob(null)
			return undefined
		}

		let cancelled = false

		fetch(url)
			.then(response => {
				if (!response.ok) {
					throw new Error('Failed to load PDF')
				}
				return response.blob()
			})
			.then(blob => {
				if (!cancelled) setPdfBlob(blob)
			})
			.catch(() => {
				if (!cancelled) {
					setLoadError('Could not display this PDF. Try Download or Open in new tab.')
				}
			})

		return () => {
			cancelled = true
		}
	}, [open, url])

	useLayoutEffect(() => {
		if (!open) return undefined
		const el = contentRef.current
		if (!el) return undefined

		const updateWidth = () => {
			const width = Math.floor(el.clientWidth)
			if (width > 0) setPageWidth(width)
		}

		updateWidth()
		const observer = new ResizeObserver(updateWidth)
		observer.observe(el)
		return () => observer.disconnect()
	}, [open, numPages])

	useEffect(() => {
		if (!open) return undefined
		const onKeyDown = e => {
			if (e.key === 'Escape') onClose()
		}
		const prevOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		window.addEventListener('keydown', onKeyDown)
		return () => {
			window.removeEventListener('keydown', onKeyDown)
			document.body.style.overflow = prevOverflow
		}
	}, [open, onClose])

	const handleDocumentLoadSuccess = useCallback(({ numPages: totalPages }) => {
		setNumPages(totalPages)
		setPageNumber(1)
		setLoadError('')
	}, [])

	const handleDocumentLoadError = useCallback(() => {
		setLoadError('Could not display this PDF. Try Download or Open in new tab.')
	}, [])

	const downloadName = `${title.replace(/[^\w\s.-]/g, '').trim() || 'document'}.pdf`
	const hasUrl = Boolean(url)

	if (!open || typeof document === 'undefined') {
		return null
	}

	return createPortal(
		<div className={s.root} role="dialog" aria-modal="true" aria-labelledby="pdf-preview-portal-title">
			<div className={s.backdrop} onClick={onClose} role="presentation" />
			<div className={s.panel} onClick={e => e.stopPropagation()}>
				<div className={s.header}>
					<span id="pdf-preview-portal-title" className={s.title}>
						{title}
					</span>
					<div className={s.actions}>
						{hasUrl ? (
							<>
								<a
									className={s.actionBtn}
									href={url}
									download={downloadName}
									onClick={e => e.stopPropagation()}>
									Download
								</a>
								<a
									className={s.actionBtn}
									href={url}
									target="_blank"
									rel="noopener noreferrer"
									onClick={e => {
										e.stopPropagation()
										if (onOpenInNewTab) {
											onOpenInNewTab()
										}
									}}>
									Open in new tab
								</a>
							</>
						) : null}
					</div>
					<button type="button" className={s.close} onClick={onClose} aria-label="Close" />
				</div>

				<div className={s.body}>
					<div className={s.document} ref={contentRef}>
						{loadError ? <p className={s.loadError}>{loadError}</p> : null}
						{hasUrl && pdfBlob ? (
							<Document
								file={pdfBlob}
								onLoadSuccess={handleDocumentLoadSuccess}
								onLoadError={handleDocumentLoadError}
								loading={
									<div className={s.loading}>
										<span className={s.spinner} aria-hidden="true" />
									</div>
								}
								error={
									<p className={s.loadError}>
										Could not display this PDF. Try Download or Open in new tab.
									</p>
								}>
								{pageWidth && numPages ? (
									<Page
										pageNumber={pageNumber}
										width={pageWidth}
										className={s.page}
										renderTextLayer={false}
										renderAnnotationLayer={false}
									/>
								) : null}
							</Document>
						) : !loadError ? (
							<div className={s.loading}>
								<span className={s.spinner} aria-hidden="true" />
							</div>
						) : null}
					</div>
				</div>

				<div className={s.footer}>
					{numPages > 1 ? (
						<div className={s.pageNav}>
							<button
								type="button"
								className={s.pageBtn}
								disabled={pageNumber <= 1}
								onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}>
								Previous
							</button>
							<span className={s.pageLabel}>
								Page {pageNumber} of {numPages}
							</span>
							<button
								type="button"
								className={s.pageBtn}
								disabled={pageNumber >= numPages}
								onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}>
								Next
							</button>
						</div>
					) : null}
				</div>
			</div>
		</div>,
		document.body,
	)
}
